import React, { useEffect, useRef, useState } from 'react'
import cn from 'classnames';
import { checkFileValidation, getAssetsToBeSelected } from '../../commons/util';
import { ListView, ThumbnailView, AssetBreadCrumb, AssetSearch } from '.';
import { ChooseAssetModalProps, GetAssetsOptions } from '../../types';
import Info from '../../../Info/Info';
import { DisplayType } from '../../types/common';

const emptyStyle = {
  color: '#4a5568',
  fontSize: '1.375rem',
  fontWeight: '800',
  letterSpacing: '-.00019em',
  lineHeight: '1.6875rem',
  marginTop: 0,
}

const NoAssets = ({ length, children, t = (key: string) => '' }) => {
  return length === 0 ? (
    <div style={{ textAlign: 'center', height: '292px', display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
      <p style={emptyStyle}>{t('AssetPicker.no_records_found') || 'No Records Found'}</p>
    </div>
  ) : children
}

interface SelectImageProps extends Omit<ChooseAssetModalProps, "onSubmit" | "closeModal" | "onCancel"> {
  isEmbed?: boolean;
  minSize?: number;
  maxSize?: number;
  setImages?: (assets: any) => void;
  t?: (key: string) => string;
}

function SelectImage(props: SelectImageProps) {
  // isEmbed is unused
  const { t = (key: string) => '', isEmbed, only, fileTypes, minSize, maxSize, excludeAssetUids, getAssets, getUsers, vendor, showUserDataColumn, showCTAIcon, multiBranchInfo, hasBranchFeature, isFolderSelection, viewOnly } = props;
  const [state, setState] = useState({
    assets: [],
    loading: false,
    itemStatusMap: {},
    loadedImageUid: {},
    displayType: props?.displayType || 'list',
    startIndex: 0,
    count: 30,
    stopIndex: 30,
    query: '',
    breadcrumb: [{
      uid: "cs_root",
      displayName: t('AssetPicker.all_assets') || 'All Assets',
      action: (idx: number, uid: string) => {
        setAssetKey('root')
        setShowSelected(false)
        setState(currState => {
          return {
            ...currState,
            breadcrumb: currState.breadcrumb.slice(0, 1),
            folderUid: 'cs_root'
          }
        })
      }
    }],
    folderUid: 'cs_root',
    users: {},
    skip: null,
    limit: 0
  });

  const [showSelected, setShowSelected] = useState(false)
  const [selectedAssets, setSelectedAssets] = useState([])
  const [selectedAssetsMap, setSelectedAssetsMap] = useState({ root: [] })
  const [assetKey, setAssetKey] = useState('root')
  const initialMount = useRef(true)
  const initialDataLoaded = useRef(false)

  /* runs only when query or folder changes, but not on initial mount */
  useEffect(() => {
    if (initialMount.current) {
      initialMount.current = false
      return // Skip initial mount, let ListView handle initial fetch
    }
    fetchOnChange({ skip: 0, limit: 30, startIndex: 0, stopIndex: 30 });
  }, [state.query, state.folderUid]);

  /* Handle initial fetch for grid view */
  useEffect(() => {
    if (state.displayType === 'grid' && !initialDataLoaded.current) {
      fetchOnChange({ skip: 0, limit: 30, startIndex: 0, stopIndex: 30 });
    }
  }, [state.displayType]);

  /* one render */
  useEffect(() => {
    if (!showUserDataColumn) return; // No Need of calling getUsers
    getUsers()
      .then(res => setState(prevState => ({ ...prevState, users: res })))
      .catch(console.error)
  }, [])

  const handleShowSelectedClick = () => {
    const selectedCount = selectedAssets.length
    if (selectedCount === 0) return
    // toggle selected view
    setShowSelected(!showSelected)
  }

  const fetchOnChange = async ({ sortBy = null, skip, limit, startIndex, stopIndex }) => {
    /* rendered here */
    const instanceItemStatusMapped = {};

    for (let i = startIndex; i <= stopIndex; i++) {
      instanceItemStatusMapped[i] = 'loading';
    }
    setState(prevState => ({
      ...prevState,
      itemStatusMap: {
        ...instanceItemStatusMapped
      },
      loading: true
    }));

    let sort: any = {}
    if (sortBy && sortBy.id && sortBy.sortingDirection) {
      sort = {
        id: sortBy.id,
        order: sortBy.sortingDirection,
      };
    }

    let response: Record<string, any>;
    const getAssetsArgs: GetAssetsOptions = {
      limit,
      skip,
      query: state.query,
      folderUid: state.folderUid,
      excludeAssetUids,
      //if sort there pass else undefined, maintaining the same old format
      sort: (sort.id && sort.order) ? sort : undefined
    };
    if (only) {
      getAssetsArgs.types = [only]
    }
    if (['asset_management'].includes(vendor)) {
      getAssetsArgs.isFolderSelection = isFolderSelection;
    }
    try {
      response = await getAssets(getAssetsArgs);
    }
    catch (error) {
      console.error(`'openChooseAssetModal': failed to get assets using 'getAssets' - ${error.message}`,);
      return;
    }


    for (let i = startIndex; i <= stopIndex; i++) {
      instanceItemStatusMapped[i] = 'loaded'
    }

    if (fileTypes || minSize || maxSize)
      response?.assets.forEach((asset: any) => {
        checkFileValidation(asset, { extensions: fileTypes, minSize, maxSize })
      })
    
    const assetsToBeSelected = getAssetsToBeSelected(response.assets, selectedAssets, props.initiallySelectedAssets);
    setSelectedAssets([...selectedAssets, ...assetsToBeSelected])
    
    /* rendered here */
    setState(prevState => ({
      ...prevState,
      assets: response.assets ? [...response.assets] : [],
      itemStatusMap: {
        ...instanceItemStatusMapped
      },
      startIndex,
      stopIndex,
      loading: false,
      count: response.count,
    }));

    // Mark that initial data has been loaded
    if (!initialDataLoaded.current) {
      initialDataLoaded.current = true;
    }
  }

  const loadMore = async ({ sortBy = null, skip, limit, startIndex, stopIndex }) => {
    // Only prevent loadMore if initial data hasn't been loaded yet
    if (!initialDataLoaded.current) return;
    
    if (state.skip === skip && state.limit === limit) return;

    let sort: any = {}
    if (sortBy && sortBy.id && sortBy.sortingDirection) {
      sort.id = sortBy.id;
      sort.order = sortBy.sortingDirection;
    }

    let response: Record<string, any>;
    const getAssetsArgs: GetAssetsOptions = {
      limit,
      skip,
      query: state.query,
      folderUid: state.folderUid,
      excludeAssetUids,
      sort: (sort.id && sort.order) ? sort : undefined
    };
    if (only) {
      getAssetsArgs.types = [only]
    }
    if (['asset_management'].includes(vendor)) {
      getAssetsArgs.isFolderSelection = isFolderSelection;
    }
    try {
      response = await getAssets(getAssetsArgs);
    }
    catch (error) {
      console.error(`'openChooseAssetModal': failed to load more assets using 'getAssets' - ${error.message}`);
      return;
    }

    const instanceItemStatusMapped = {};

    for (let i = startIndex; i <= stopIndex; i++) {
      instanceItemStatusMapped[i] = 'loaded';
    }

    if (fileTypes)
      response?.assets.forEach((asset: any) => {
        checkFileValidation(asset, { extensions: fileTypes })
      })

    const assetsToBeSelected = getAssetsToBeSelected(response.assets, selectedAssets, props.initiallySelectedAssets);
    setSelectedAssets([...selectedAssets, ...assetsToBeSelected])

    setState(prevState => {
      // Deduplicate assets by uid to prevent duplicates
      const existingUids = new Set(prevState.assets.map(asset => asset.uid));
      const newAssets = response.assets.filter(asset => !existingUids.has(asset.uid));
      
      return {
        ...prevState,
        assets: [
          ...prevState.assets,
          ...newAssets
        ],
        count: response.count,
        itemStatusMap: {
          ...prevState.itemStatusMap,
          ...instanceItemStatusMapped
        },
        startIndex,
        stopIndex,
        skip,
        limit
      }
    })

  }

  const handleClick = (asset) => {
    if (asset && asset.is_dir) {
      setAssetKey(asset.uid)
      setShowSelected(false)
      setState(currState => ({
        ...currState,
        breadcrumb: [...currState.breadcrumb, {
          uid: asset.uid,
          displayName: asset.name,
          action: (len = state.breadcrumb.length) => {
            setState(prevState => {
              return {
                ...prevState,
                breadcrumb: prevState.breadcrumb.slice(0, len + 1),
                folderUid: asset.uid,
                query: ''
              }
            });
          }
        }],
        folderUid: asset.uid,
        query: ''
      }));
    } else if (!props?.multiple) {
      props.setImages([asset])
      setSelectedAssets([asset])
    }
  }

  const updateSelection = (selectedAssets) => {
    setSelectedAssets(selectedAssets)
    props.setImages(selectedAssets)
  }

  const loadMoreGrid = () => {
    const skip = state.stopIndex;
    const limit = 30;
    const startIndex = state.stopIndex + 1;
    const stopIndex = startIndex + limit;
    loadMore({ skip, startIndex, limit, stopIndex });
  }

  const selectedCount = selectedAssets.length
  const selectedAssetsClassNames = cn('selected-assets', {
    'selected-assets__disabled': selectedCount === 0
  })

  const getChangeSelectionViewBox = () => {
    if (showSelected) return (<span>{t('AssetPicker.show_all_assets') || 'Show all assets'} ({state.count})</span>)
    else return (<span>{t('AssetPicker.show_selected_assets') || 'Show selected assets'} ({selectedAssets.length})</span>)
  }

  const onQueryChange = (queryText: string) => {
    setState((prev) => ({ ...prev, query: queryText }))
  }

  const onDisplayTypeChange = (displayType: DisplayType) => {
    if (props?.onDisplayTypeChange) {
      props?.onDisplayTypeChange(displayType)
    }
    setState((prev) => ({ ...prev, displayType }))
  }

  return (
    <div style={{ fontFamily: 'Inter' }} data-testid="select-image-wrapper" data-test-id="select-image-wrapper">
      {hasBranchFeature && multiBranchInfo ? <div className="modal-multibranch-info"><Info version="v2" type="light" content={multiBranchInfo} /></div> : null}
      <div className="asset-box">
        <AssetBreadCrumb breadcrumbs={state.breadcrumb} showIcon={showCTAIcon} />
        <div className={selectedAssetsClassNames} onClick={handleShowSelectedClick} data-testid="cs-selected-assets">
          {!viewOnly && getChangeSelectionViewBox()}
        </div>
      </div>
      <AssetSearch t={t} showSearchBox={!showSelected} selectedAssetsCount={selectedCount} assetsCount={state.count} listType={state.displayType} onDisplayTypeChange={onDisplayTypeChange} onQueryChange={onQueryChange} />
      <NoAssets t={t} length={state.count}>
        {
          state.displayType === 'grid' ? (
            <ThumbnailView
              readOnly={showSelected}
              // @ts-ignore
              assetKey={assetKey}
              selectedAssetMap={selectedAssetsMap}
              setSelectedAssetsMap={setSelectedAssetsMap}
              selectedAssets={selectedAssets}
              updateSelection={updateSelection}
              multiple={props?.multiple}
              state={state}
              handleClick={handleClick}
              loadMoreGrid={loadMoreGrid}
              vendor={vendor}
              hasBranchFeature={hasBranchFeature}
              isFolderSelection={isFolderSelection}
              viewOnly={viewOnly}
            />
          ) :
            <ListView
              t={t}
              assetKey={assetKey}
              selectedAssetsMap={selectedAssetsMap}
              setSelectedAssetsMap={setSelectedAssetsMap}
              selectedAssets={selectedAssets}
              readOnly={showSelected}
              updateSelection={updateSelection}
              multiple={props?.multiple}
              users={state.users}
              loading={state.loading}
              state={state}
              handleClick={handleClick}
              fetchOnChange={fetchOnChange}
              loadMore={loadMore}
              vendor={vendor}
              showUserDataColumn={showUserDataColumn}
              hasBranchFeature={hasBranchFeature}
              isFolderSelection={isFolderSelection}
              viewOnly={viewOnly}
            />
        }
      </NoAssets>

    </div>
  )
}


export default SelectImage;
